// src/features/calendar/EventDialog.tsx
// Create / edit an event covering the full CalendarEvent surface:
// schedule, recurrence, attendees, location, conference, reminders,
// guest permissions, tags, visibility, color, templates and "find a time".

import { useEffect, useMemo, useState } from 'react';
import { observer } from '@react';
import { useCalendarEvent, useCalendarTemplate, useI18n } from '@react/hooks';
import { Button, Input, Textarea, Alert } from '@react/components';
import { Select } from '@/components/Select';
import { UserPicker } from '@/components/UserPicker';
import { useScope } from '@/hooks/useScope';
import type { Actor } from '@core/types/actor';
import type {
  Calendar,
  CalendarEvent,
  ConferenceType,
  EventReminder,
  EventType,
  EventVisibility,
  RecurrenceRule,
  RecurrenceUpdateScope,
  ReminderMethod,
} from '@core/types/calendar';
import { ModalShell } from './ModalShell';
import { RecurrenceEditor } from './RecurrenceEditor';
import { FindTimePanel } from './FindTimePanel';
import {
  dateTimeToIso,
  errMessage,
  isRecurring,
  makeEntity,
  toDateInputValue,
  toTimeInputValue,
} from './time';

export interface EventDialogProps {
  open: boolean;
  onClose: () => void;
  /** Existing event to edit; null → create. */
  event: CalendarEvent | null;
  calendars: Calendar[];
  defaultCalendarId?: string;
  /** Prefill for click-to-create on a slot. */
  initialStart?: Date;
  onSaved: () => void;
}

const EVENT_TYPES: EventType[] = [
  'EVENT_TYPE_EVENT',
  'EVENT_TYPE_MEETING',
  'EVENT_TYPE_FOCUS_TIME',
  'EVENT_TYPE_OUT_OF_OFFICE',
  'EVENT_TYPE_WORKING_LOCATION',
  'EVENT_TYPE_TASK_DEADLINE',
  'EVENT_TYPE_REMINDER',
  'EVENT_TYPE_APPOINTMENT',
];

const VISIBILITIES: EventVisibility[] = [
  'EVENT_VISIBILITY_DEFAULT',
  'EVENT_VISIBILITY_PUBLIC',
  'EVENT_VISIBILITY_PRIVATE',
  'EVENT_VISIBILITY_CONFIDENTIAL',
];

const CONFERENCE_TYPES: ConferenceType[] = [
  'CONFERENCE_TYPE_CUSTOM',
  'CONFERENCE_TYPE_MEET',
  'CONFERENCE_TYPE_ZOOM',
  'CONFERENCE_TYPE_TEAMS',
  'CONFERENCE_TYPE_WEBEX',
];

const REMINDER_METHODS: ReminderMethod[] = [
  'REMINDER_METHOD_IN_APP',
  'REMINDER_METHOD_EMAIL',
  'REMINDER_METHOD_PUSH',
  'REMINDER_METHOD_SMS',
];

const enumLabelKey = (v: string) => v.toLowerCase();

/** Field paths sent in update masks — everything this form edits. */
const EDITABLE_PATHS = [
  'title', 'description', 'event_type', 'start_time', 'end_time', 'is_all_day', 'timezone',
  'visibility', 'color', 'location', 'conference', 'reminders', 'use_default_reminders',
  'guest_can_modify', 'guest_can_invite_others', 'guest_can_see_other_guests', 'tags',
];

export const EventDialog = observer(({
  open, onClose, event, calendars, defaultCalendarId, initialStart, onSaved,
}: EventDialogProps) => {
  const { t } = useI18n();
  const scope = useScope();
  const eventStore = useCalendarEvent();
  const templateStore = useCalendarTemplate();

  const isEdit = !!event;

  // --- form state ---
  const [title, setTitle] = useState('');
  const [calendarId, setCalendarId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [timeStart, setTimeStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [timeEnd, setTimeEnd] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [timezone, setTimezone] = useState('');
  const [eventType, setEventType] = useState<EventType>('EVENT_TYPE_MEETING');
  const [visibility, setVisibility] = useState<EventVisibility>('EVENT_VISIBILITY_DEFAULT');
  const [color, setColor] = useState('');
  const [description, setDescription] = useState('');
  const [attendees, setAttendees] = useState<Actor[]>([]);
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [confType, setConfType] = useState<ConferenceType | ''>('');
  const [confUri, setConfUri] = useState('');
  const [reminders, setReminders] = useState<EventReminder[]>([]);
  const [useDefaultReminders, setUseDefaultReminders] = useState(true);
  const [guestModify, setGuestModify] = useState(false);
  const [guestInvite, setGuestInvite] = useState(false);
  const [guestSee, setGuestSee] = useState(true);
  const [tags, setTags] = useState('');
  const [rule, setRule] = useState<RecurrenceRule | null>(null);
  const [recScope, setRecScope] = useState<RecurrenceUpdateScope>('RECURRENCE_UPDATE_SCOPE_THIS_INSTANCE');
  const [showFindTime, setShowFindTime] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- init on open ---
  useEffect(() => {
    if (!open) return;
    setError(null);
    setTemplateId('');
    setShowFindTime(false);
    setRecScope('RECURRENCE_UPDATE_SCOPE_THIS_INSTANCE');
    if (event) {
      const s = event.start_time ? new Date(event.start_time) : new Date();
      const e = event.end_time ? new Date(event.end_time) : new Date(s.getTime() + 3600000);
      setTitle(event.title ?? '');
      setCalendarId(event.calendar?.id ?? defaultCalendarId ?? '');
      setDateStart(toDateInputValue(s));
      setTimeStart(toTimeInputValue(s));
      setDateEnd(toDateInputValue(e));
      setTimeEnd(toTimeInputValue(e));
      setAllDay(event.is_all_day ?? false);
      setTimezone(event.timezone ?? '');
      setEventType(event.event_type ?? 'EVENT_TYPE_MEETING');
      setVisibility(event.visibility ?? 'EVENT_VISIBILITY_DEFAULT');
      setColor(event.color ?? '');
      setDescription(event.description ?? '');
      setAttendees((event.attendees?.attendees ?? []).map((a) => a.attendee).filter((a): a is Actor => !!a));
      setLocationName(event.location?.display_name ?? '');
      setLocationAddress(event.location?.address ?? '');
      setConfType(event.conference?.type ?? '');
      setConfUri(event.conference?.uri ?? '');
      setReminders(event.reminders?.reminders ?? []);
      setUseDefaultReminders(event.use_default_reminders ?? true);
      setGuestModify(event.guest_can_modify ?? false);
      setGuestInvite(event.guest_can_invite_others ?? false);
      setGuestSee(event.guest_can_see_other_guests ?? true);
      setTags((event.tags ?? []).join(', '));
      setRule(event.recurrence_rule ?? null);
      setShowDetails(true);
    } else {
      const s = initialStart ?? new Date();
      const e = new Date(s.getTime() + 3600000);
      setTitle('');
      setCalendarId(defaultCalendarId ?? calendars[0]?.entity?.id ?? '');
      setDateStart(toDateInputValue(s));
      setTimeStart(toTimeInputValue(s));
      setDateEnd(toDateInputValue(e));
      setTimeEnd(toTimeInputValue(e));
      setAllDay(false);
      setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone ?? '');
      setEventType('EVENT_TYPE_MEETING');
      setVisibility('EVENT_VISIBILITY_DEFAULT');
      setColor('');
      setDescription('');
      setAttendees([]);
      setLocationName('');
      setLocationAddress('');
      setConfType('');
      setConfUri('');
      setReminders([]);
      setUseDefaultReminders(true);
      setGuestModify(false);
      setGuestInvite(false);
      setGuestSee(true);
      setTags('');
      setRule(null);
      setShowDetails(false);
    }
  }, [open, event?.entity?.id]);

  // Templates for "from template" (create mode only).
  useEffect(() => {
    if (open && !isEdit) {
      templateStore.ListEventTemplates(scope.productId).catch(() => {});
    }
  }, [open, isEdit]);

  const durationMinutes = useMemo(() => {
    if (!dateStart || !dateEnd) return 60;
    const s = new Date(dateTimeToIso(dateStart, timeStart));
    const e = new Date(dateTimeToIso(dateEnd, timeEnd));
    const mins = Math.round((e.getTime() - s.getTime()) / 60000);
    return mins > 0 ? mins : 60;
  }, [dateStart, timeStart, dateEnd, timeEnd]);

  const buildCommon = () => ({
    title: title.trim(),
    description: description.trim() || undefined,
    event_type: eventType,
    start_time: allDay ? dateTimeToIso(dateStart, '00:00') : dateTimeToIso(dateStart, timeStart),
    end_time: allDay ? dateTimeToIso(dateEnd, '23:59') : dateTimeToIso(dateEnd, timeEnd),
    is_all_day: allDay,
    timezone: timezone || undefined,
    visibility,
    color: color || undefined,
    attendees,
    location: locationName.trim()
      ? { display_name: locationName.trim(), address: locationAddress.trim() || undefined }
      : undefined,
    conference: confUri.trim()
      ? { type: (confType || 'CONFERENCE_TYPE_CUSTOM') as ConferenceType, uri: confUri.trim(), phone_numbers: [] }
      : undefined,
    reminders,
    use_default_reminders: useDefaultReminders,
    guest_can_modify: guestModify,
    guest_can_invite_others: guestInvite,
    guest_can_see_other_guests: guestSee,
    tags: tags.split(',').map((s) => s.trim()).filter(Boolean),
  });

  const save = async () => {
    setError(null);
    if (!templateId && !title.trim()) {
      setError(t('calendar.event.titleRequired'));
      return;
    }
    if (!calendarId) {
      setError(t('calendar.event.calendarRequired'));
      return;
    }
    setIsSaving(true);
    try {
      const calendarEntity = makeEntity(calendarId, 'calendar', scope);
      const common = buildCommon();
      if (isEdit && event) {
        const updated: CalendarEvent = {
          ...event,
          ...common,
          timezone: common.timezone ?? '',
          attendees: event.attendees, // attendee changes go through invite/remove RPCs
          reminders: { reminders },
          tags: common.tags,
        };
        if (isRecurring(event)) {
          await eventStore.UpdateRecurringEvent(event.entity?.id ?? '', {
            event: event.entity,
            updated_event: updated,
            field_mask: EDITABLE_PATHS,
            scope: recScope,
          });
        } else {
          await eventStore.UpdateEvent(event.entity?.id ?? '', {
            event: updated,
            field_mask: EDITABLE_PATHS,
          });
        }
      } else if (templateId) {
        await eventStore.CreateEventFromTemplate(templateId, {
          template: makeEntity(templateId, 'event_template', scope),
          calendar: calendarEntity,
          start_time: common.start_time,
          end_time: common.end_time,
          title_override: title.trim() || undefined,
          additional_attendees: attendees,
        });
      } else if (rule) {
        await eventStore.CreateRecurringEvent(calendarId, {
          calendar: calendarEntity,
          ...common,
          recurrence_rule: rule,
        });
      } else {
        await eventStore.CreateEvent(calendarId, {
          calendar: calendarEntity,
          ...common,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('calendar.event.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  const calendarOptions = calendars.map((c) => ({
    value: c.entity?.id ?? '',
    label: c.name,
  }));

  const templateOptions = [
    { value: '', label: t('calendar.event.noTemplate') },
    ...templateStore.items.map((tpl) => ({ value: tpl.entity?.id ?? '', label: tpl.name })),
  ];

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={isEdit ? t('calendar.event.editTitle') : t('calendar.event.createTitle')}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={save} isLoading={isSaving}>
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        {!isEdit && templateStore.items.length > 0 && (
          <Select
            label={t('calendar.event.fromTemplate')}
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            options={templateOptions}
            helperText={templateId ? t('calendar.event.templateHint') : undefined}
          />
        )}

        <Input
          label={t('calendar.event.title')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('calendar.event.titlePlaceholder')}
          required={!templateId}
        />

        <Select
          label={t('calendar.event.calendar')}
          value={calendarId}
          onChange={(e) => setCalendarId(e.target.value)}
          options={calendarOptions}
          placeholder={t('calendar.event.calendarPlaceholder')}
        />

        {/* Schedule */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <label className="flex flex-col gap-1 text-sm font-medium text-foreground">
            {t('calendar.event.startDate')}
            <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground" />
          </label>
          {!allDay && (
            <label className="flex flex-col gap-1 text-sm font-medium text-foreground">
              {t('calendar.event.startTime')}
              <input type="time" value={timeStart} onChange={(e) => setTimeStart(e.target.value)}
                className="rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground" />
            </label>
          )}
          <label className="flex flex-col gap-1 text-sm font-medium text-foreground">
            {t('calendar.event.endDate')}
            <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground" />
          </label>
          {!allDay && (
            <label className="flex flex-col gap-1 text-sm font-medium text-foreground">
              {t('calendar.event.endTime')}
              <input type="time" value={timeEnd} onChange={(e) => setTimeEnd(e.target.value)}
                className="rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground" />
            </label>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
            {t('calendar.event.allDay')}
          </label>
          <button
            type="button"
            onClick={() => setShowFindTime((v) => !v)}
            className="text-sm text-primary hover:underline"
            aria-expanded={showFindTime}
          >
            {t('calendar.findTime.toggle')}
          </button>
        </div>

        {showFindTime && (
          <FindTimePanel
            attendees={attendees}
            durationMinutes={durationMinutes}
            onPick={(s, e) => {
              const sd = new Date(s);
              const ed = new Date(e);
              setDateStart(toDateInputValue(sd));
              setTimeStart(toTimeInputValue(sd));
              setDateEnd(toDateInputValue(ed));
              setTimeEnd(toTimeInputValue(ed));
              setShowFindTime(false);
            }}
          />
        )}

        {/* Recurrence: full editor on create; scope choice on recurring edit */}
        {!isEdit && !templateId && <RecurrenceEditor value={rule} onChange={setRule} />}
        {isEdit && event && isRecurring(event) && (
          <fieldset className="space-y-1 rounded-md border border-border p-3">
            <legend className="px-1 text-sm font-medium text-foreground">{t('calendar.series.applyTo')}</legend>
            {(['RECURRENCE_UPDATE_SCOPE_THIS_INSTANCE', 'RECURRENCE_UPDATE_SCOPE_THIS_AND_FOLLOWING', 'RECURRENCE_UPDATE_SCOPE_ALL_INSTANCES'] as RecurrenceUpdateScope[]).map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="radio"
                  name="recurrence-scope"
                  checked={recScope === s}
                  onChange={() => setRecScope(s)}
                />
                {t(`calendar.series.${enumLabelKey(s.replace('RECURRENCE_UPDATE_SCOPE_', ''))}`)}
              </label>
            ))}
          </fieldset>
        )}

        {/* Attendees */}
        <div className="space-y-2">
          <span className="block text-sm font-medium text-foreground">{t('calendar.event.attendees')}</span>
          {attendees.length > 0 && (
            <ul className="space-y-1">
              {attendees.map((a) => (
                <li key={a.entity?.id} className="flex items-center justify-between rounded-md border border-border px-2 py-1 text-sm text-foreground">
                  <span>{a.display_name || a.entity?.id}</span>
                  <button
                    type="button"
                    aria-label={t('calendar.event.removeAttendee')}
                    onClick={() => setAttendees((prev) => prev.filter((x) => x.entity?.id !== a.entity?.id))}
                    className="rounded-md p-0.5 text-muted-foreground hover:text-destructive"
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <UserPicker
            value=""
            placeholder={t('calendar.event.addAttendee')}
            onChange={(userId, displayName) => {
              if (!userId || attendees.some((a) => a.entity?.id === userId)) return;
              setAttendees((prev) => [
                ...prev,
                { entity: makeEntity(userId, 'profile', scope), display_name: displayName, avatar: '' },
              ]);
            }}
          />
          {isEdit && (
            <p className="text-xs text-muted-foreground">{t('calendar.event.attendeeEditHint')}</p>
          )}
        </div>

        {/* Details (collapsible) */}
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          aria-expanded={showDetails}
          className="text-sm text-primary hover:underline"
        >
          {showDetails ? t('calendar.event.hideDetails') : t('calendar.event.showDetails')}
        </button>

        {showDetails && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Select
                label={t('calendar.event.type')}
                value={eventType}
                onChange={(e) => setEventType(e.target.value as EventType)}
                options={EVENT_TYPES.map((v) => ({ value: v, label: t(`calendar.eventType.${enumLabelKey(v.replace('EVENT_TYPE_', ''))}`) }))}
              />
              <Select
                label={t('calendar.event.visibility')}
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as EventVisibility)}
                options={VISIBILITIES.map((v) => ({ value: v, label: t(`calendar.visibilityValue.${enumLabelKey(v.replace('EVENT_VISIBILITY_', ''))}`) }))}
              />
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input
                label={t('calendar.event.timezone')}
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="Europe/Amsterdam"
              />
              <Input
                label={t('calendar.event.color')}
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#4f46e5"
              />
            </div>
            <Textarea
              label={t('calendar.event.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input
                label={t('calendar.event.location')}
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                placeholder={t('calendar.event.locationPlaceholder')}
              />
              <Input
                label={t('calendar.event.address')}
                value={locationAddress}
                onChange={(e) => setLocationAddress(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Select
                label={t('calendar.event.conferenceType')}
                value={confType}
                onChange={(e) => setConfType(e.target.value as ConferenceType)}
                placeholder={t('common.none')}
                options={CONFERENCE_TYPES.map((v) => ({ value: v, label: t(`calendar.conference.${enumLabelKey(v.replace('CONFERENCE_TYPE_', ''))}`) }))}
              />
              <Input
                label={t('calendar.event.conferenceUri')}
                value={confUri}
                onChange={(e) => setConfUri(e.target.value)}
                placeholder="https://…"
              />
            </div>

            {/* Reminders */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={useDefaultReminders}
                  onChange={(e) => setUseDefaultReminders(e.target.checked)}
                />
                {t('calendar.event.useDefaultReminders')}
              </label>
              {!useDefaultReminders && (
                <div className="space-y-1.5">
                  {reminders.map((r, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Select
                        aria-label={t('calendar.event.reminderMethod')}
                        value={r.method ?? 'REMINDER_METHOD_IN_APP'}
                        onChange={(e) => setReminders((prev) => prev.map((x, j) => (j === i ? { ...x, method: e.target.value as ReminderMethod } : x)))}
                        options={REMINDER_METHODS.map((v) => ({ value: v, label: t(`calendar.reminderMethod.${enumLabelKey(v.replace('REMINDER_METHOD_', ''))}`) }))}
                        fullWidth={false}
                        className="w-40"
                      />
                      <Input
                        aria-label={t('calendar.event.reminderMinutes')}
                        type="text"
                        inputMode="numeric"
                        fullWidth={false}
                        className="w-24"
                        value={r.minutes_before > 0 || r.minutes_before === 0 ? String(r.minutes_before) : ''}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9]/g, '');
                          setReminders((prev) => prev.map((x, j) => (j === i ? { ...x, minutes_before: v === '' ? 0 : parseInt(v, 10) } : x)));
                        }}
                      />
                      <span className="text-sm text-muted-foreground">{t('calendar.event.minutesBefore')}</span>
                      <button
                        type="button"
                        aria-label={t('calendar.event.removeReminder')}
                        onClick={() => setReminders((prev) => prev.filter((_, j) => j !== i))}
                        className="rounded-md p-1 text-muted-foreground hover:text-destructive"
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setReminders((prev) => [...prev, { method: 'REMINDER_METHOD_IN_APP', minutes_before: 10 }])}
                  >
                    {t('calendar.event.addReminder')}
                  </Button>
                </div>
              )}
            </div>

            {/* Guest permissions */}
            <fieldset className="space-y-1 rounded-md border border-border p-3">
              <legend className="px-1 text-sm font-medium text-foreground">{t('calendar.event.guestPermissions')}</legend>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={guestModify} onChange={(e) => setGuestModify(e.target.checked)} />
                {t('calendar.event.guestCanModify')}
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={guestInvite} onChange={(e) => setGuestInvite(e.target.checked)} />
                {t('calendar.event.guestCanInvite')}
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={guestSee} onChange={(e) => setGuestSee(e.target.checked)} />
                {t('calendar.event.guestCanSeeOthers')}
              </label>
            </fieldset>

            <Input
              label={t('calendar.event.tags')}
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              helperText={t('calendar.event.tagsHint')}
            />
          </div>
        )}
      </div>
    </ModalShell>
  );
});
